import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
/**
 * Sync the hidden variant id on the installments form input with the variant
 * id from a product-select response, then fire a bubbling `change` event so
 * the form's own listeners re-evaluate price/installments display.
 *
 * Pure: only touches the input it is given; mirrors the original handler
 * (which set `value` to the resource id or empty string and dispatched the
 * same `change` event).
 *
 * @param {HTMLInputElement} input
 * @param {string | number | null | undefined} variantId
 * @returns {void}
 */ function syncInstallmentsVariant(input, variantId) {
  input.value = variantId ?? '';
  input.dispatchEvent(new Event('change', { bubbles: true }));
}
class ProductPrice extends Component {
  #sectionTarget = null;
  /**
   * Lifecycle: locates the closest `.shopify-section` or `<dialog>` (which
   * is the actual event target — this element listens on the section so it
   * survives even when the inner product cards re-render) and subscribes to
   * `StandardEvents.productSelect`. No-op when the element is mounted
   * outside any of those containers.
   *
   * @returns {void}
   */
  connectedCallback() {
    super.connectedCallback();
    this.#sectionTarget = this.closest('.shopify-section, dialog');
    if (!this.#sectionTarget) return;
    this.#sectionTarget.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
    );
  }
  /**
   * Lifecycle: detaches the `productSelect` listener and clears the cached
   * section target. No-op when the element was never bound to a section.
   *
   * @returns {void}
   */ disconnectedCallback() {
    super.disconnectedCallback();
    if (!this.#sectionTarget) return;
    this.#sectionTarget.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
    );
    this.#sectionTarget = null;
  }
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    )
      return;
    event.promise
      .then(({ detail: detail }) => {
        if (!detail?.html) return;
        const { html: html, newProduct: newProduct } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (
          detail.productId &&
          detail.productId !== this.dataset.productId
        ) {
          return;
        }
        const {
          priceContainer: priceContainer,
          volumePricingNote: volumePricingNote,
        } = this.refs;
        const newProductPrice = html.querySelector(
          `product-price[data-block-id="${this.dataset.blockId}"]`,
        );
        if (!newProductPrice) return;
        const newPrice = newProductPrice.querySelector(
          '[ref="priceContainer"]',
        );
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }
        const newNote = newProductPrice.querySelector(
          '[ref="volumePricingNote"]',
        );
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          newPrice?.insertAdjacentElement('afterend', newNote.cloneNode(true));
        } else {
          volumePricingNote.replaceWith(newNote);
        }
        const installmentsInput = this.querySelector(
          `#product-form-installment-${this.dataset.blockId} input[name="id"]`,
        );
        if (installmentsInput) {
          syncInstallmentsVariant(installmentsInput, detail.resource?.id);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError')
          console.warn('[product-price] Event promise rejected:', error);
      });
  };
}
if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
